/**
 * sl-mkx.5.2: Publish-Wizard "Trigger Review" sub-panel
 *
 * Renders only for kind='automation'. Fetches candidate trigger fields
 * (cron / webhook / db_column) from the backend sanitizer and lets the
 * publisher decide per candidate:
 *
 *   - keep: publish the concrete value as-is (default)
 *   - parametrize: replace with install-time placeholder + default
 *   - generate: mint a value at install (webhook_url only)
 *
 * The resulting overrides are emitted via `onOverridesChange` for the
 * wizard to send with the publish body. Unknown/drifted refs are
 * silently ignored by the backend — fail-soft.
 */
import { useCallback, useEffect, useState } from 'react';

import { workspaceApiFetch } from '../../../modules/cloud';
import * as styles from './trigger-review-step.css';

export type TriggerFieldKind = 'cron' | 'webhook_url' | 'db_column';

export interface TriggerCandidate {
  fieldRef: string;
  kind: TriggerFieldKind;
  originalValue: string;
  suggestedLabel: string;
}

export interface ParameterizedTriggerOverride {
  fieldRef: string;
  kind: TriggerFieldKind;
  label: string;
  default?: string;
  required: boolean;
}

type Decision = 'keep' | 'parametrize' | 'generate';

interface CandidateDraft {
  decision: Decision;
  label: string;
  default: string;
  required: boolean;
}

const GENERATE_AT_INSTALL = 'GENERATE_AT_INSTALL';

function initialDraft(candidate: TriggerCandidate): CandidateDraft {
  return {
    decision: candidate.kind === 'webhook_url' ? 'generate' : 'keep',
    label: candidate.suggestedLabel,
    default: candidate.kind === 'webhook_url' ? '' : candidate.originalValue,
    required: candidate.kind !== 'webhook_url',
  };
}

function toOverrides(
  candidates: TriggerCandidate[],
  drafts: Record<string, CandidateDraft>
): ParameterizedTriggerOverride[] {
  const out: ParameterizedTriggerOverride[] = [];
  for (const c of candidates) {
    const draft = drafts[c.fieldRef];
    if (!draft || draft.decision === 'keep') continue;
    const isGenerate = draft.decision === 'generate';
    out.push({
      fieldRef: c.fieldRef,
      kind: c.kind,
      label: draft.label.trim() || c.suggestedLabel,
      default: isGenerate
        ? GENERATE_AT_INSTALL
        : draft.default.trim() || undefined,
      required: isGenerate ? false : draft.required,
    });
  }
  return out;
}

export interface TriggerReviewStepProps {
  workspaceId: string;
  automationBlockId: string;
  onOverridesChange: (overrides: ParameterizedTriggerOverride[]) => void;
}

export function TriggerReviewStep({
  workspaceId,
  automationBlockId,
  onOverridesChange,
}: TriggerReviewStepProps) {
  const [candidates, setCandidates] = useState<TriggerCandidate[]>([]);
  const [drafts, setDrafts] = useState<Record<string, CandidateDraft>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await workspaceApiFetch(
          `/api/workspace/${workspaceId}/marketplace/analyze-triggers/${automationBlockId}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { candidates: TriggerCandidate[] };
        if (cancelled) return;
        setCandidates(body.candidates);
        const seeded: Record<string, CandidateDraft> = {};
        for (const c of body.candidates) {
          seeded[c.fieldRef] = initialDraft(c);
        }
        setDrafts(seeded);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Unknown error');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, automationBlockId]);

  useEffect(() => {
    onOverridesChange(toOverrides(candidates, drafts));
  }, [candidates, drafts, onOverridesChange]);

  const updateDraft = useCallback(
    (fieldRef: string, patch: Partial<CandidateDraft>) => {
      setDrafts(prev => ({
        ...prev,
        [fieldRef]: { ...prev[fieldRef], ...patch },
      }));
    },
    []
  );

  if (isLoading) {
    return <div className={styles.emptyState}>Analyzing triggers...</div>;
  }
  if (loadError) {
    return (
      <div className={styles.emptyState}>
        Could not analyze triggers: {loadError}
      </div>
    );
  }
  if (candidates.length === 0) {
    return (
      <div className={styles.emptyState}>
        No cron, webhook or database-column triggers detected. Nothing to
        parametrize.
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.headline}>Trigger review</div>
      <div className={styles.subline}>
        Decide per trigger whether installers inherit the value, supply their
        own, or receive a fresh one at install time.
      </div>

      {candidates.map(candidate => {
        const draft = drafts[candidate.fieldRef];
        if (!draft) return null;
        const isWebhook = candidate.kind === 'webhook_url';
        return (
          <div key={candidate.fieldRef} className={styles.candidateCard}>
            <div className={styles.candidateHeader}>
              <span className={styles.candidateTitle}>
                {candidate.suggestedLabel}
              </span>
              <span className={styles.candidateMeta}>
                {candidate.kind} · {candidate.fieldRef}
              </span>
            </div>

            <label
              className={
                isWebhook
                  ? `${styles.optionRow} ${styles.optionRowDisabled}`
                  : styles.optionRow
              }
            >
              <input
                type="radio"
                name={`decision-${candidate.fieldRef}`}
                checked={draft.decision === 'keep'}
                disabled={isWebhook}
                onChange={() =>
                  updateDraft(candidate.fieldRef, { decision: 'keep' })
                }
              />
              Keep as-is (publish with current value)
            </label>

            <label className={styles.optionRow}>
              <input
                type="radio"
                name={`decision-${candidate.fieldRef}`}
                checked={draft.decision === 'parametrize'}
                onChange={() =>
                  updateDraft(candidate.fieldRef, { decision: 'parametrize' })
                }
              />
              Prompt installer (with default)
            </label>

            {isWebhook && (
              <label className={styles.optionRow}>
                <input
                  type="radio"
                  name={`decision-${candidate.fieldRef}`}
                  checked={draft.decision === 'generate'}
                  onChange={() =>
                    updateDraft(candidate.fieldRef, { decision: 'generate' })
                  }
                />
                Generate fresh at install (workspace-scoped webhook URL)
              </label>
            )}

            {draft.decision !== 'keep' && (
              <>
                <input
                  type="text"
                  className={styles.labelInput}
                  placeholder="Installer-facing label"
                  value={draft.label}
                  onChange={e =>
                    updateDraft(candidate.fieldRef, { label: e.target.value })
                  }
                />
                {draft.decision === 'parametrize' && (
                  <input
                    type="text"
                    className={styles.labelInput}
                    placeholder={
                      candidate.kind === 'cron'
                        ? 'Default cron expression (optional)'
                        : candidate.kind === 'db_column'
                          ? 'Default database column (optional)'
                          : 'Default value (optional)'
                    }
                    value={draft.default}
                    onChange={e =>
                      updateDraft(candidate.fieldRef, {
                        default: e.target.value,
                      })
                    }
                  />
                )}
                {draft.decision === 'parametrize' && (
                  <label className={styles.optionRow}>
                    <input
                      type="checkbox"
                      checked={draft.required}
                      onChange={e =>
                        updateDraft(candidate.fieldRef, {
                          required: e.target.checked,
                        })
                      }
                    />
                    Installer must provide a value
                  </label>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
